import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {Alert, Button, Card, Modal, Spinner, Table} from "react-bootstrap"
import {useAuth} from "../../context/AuthProvider"
import {Eexam} from "./types"
import {deleteEexam, loadEexams, saveEexam} from "./api/eexam.api"
import EexamForm from "./forms/EexamForm"

const emptyEexam = (): Partial<Eexam> => ({title: {de: "", en: ""}, draftingCourseId: undefined, moduleHandbookFile: ""})

type Props = {canWrite: boolean}

export default function EexamManagement({canWrite}: Props) {
    const auth = useAuth()
    const {t, i18n} = useTranslation(["eexam"])
    const [items, setItems] = useState<Eexam[]>([])
    const [current, setCurrent] = useState<Partial<Eexam>>(emptyEexam())
    const [showModal, setShowModal] = useState(false)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const lang = i18n.language.startsWith("en") ? "en" : "de"

    const load = async () => {
        setLoading(true)
        setError(null)
        try { setItems(await loadEexams(auth.axiosAuthHeader())) } catch { setError(t("messages.loadError")) } finally { setLoading(false) }
    }

    useEffect(() => { load() }, [])

    const openCreate = () => { setCurrent(emptyEexam()); setShowModal(true) }
    const openEdit = (item: Eexam) => { setCurrent(item); setShowModal(true) }
    const save = async () => {
        setSaving(true)
        try { await saveEexam(current, auth.axiosAuthHeader()); setShowModal(false); await load() } catch { setError(t("messages.saveError")) } finally { setSaving(false) }
    }
    const remove = async (item: Eexam) => {
        if (!window.confirm(t("messages.confirmDelete"))) return
        setSaving(true)
        try { await deleteEexam(item._id, auth.axiosAuthHeader()); await load() } catch { setError(t("messages.deleteError")) } finally { setSaving(false) }
    }

    if (loading) return <Card><Card.Body className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm"/>{t("messages.loading")}</Card.Body></Card>

    return (
        <>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>{t("eexam.title")}</strong>
                    {canWrite && <Button size="sm" onClick={openCreate}><i className="bi bi-plus-lg me-1"/>{t("actions.create")}</Button>}
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Table responsive hover>
                        <thead><tr><th>{t("fields.title")}</th><th>{t("fields.draftingCourseId")}</th><th>{t("fields.moduleHandbookFile")}</th><th className="text-end">{t("actions.actions")}</th></tr></thead>
                        <tbody>
                        {items.map(item => <tr key={item._id}><td>{item.title?.[lang]}</td><td>{item.draftingCourseId ?? "–"}</td><td>{item.moduleHandbookFile || "–"}</td><td className="text-end">{canWrite && <><Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(item)}>{t("actions.edit")}</Button><Button size="sm" variant="outline-danger" disabled={saving} onClick={() => remove(item)}>{t("actions.delete")}</Button></>}</td></tr>)}
                        {items.length === 0 && <tr><td colSpan={4} className="text-muted">{t("messages.empty")}</td></tr>}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton><Modal.Title>{current._id ? t("eexam.editTitle") : t("eexam.createTitle")}</Modal.Title></Modal.Header>
                <Modal.Body><EexamForm value={current} onChange={setCurrent}/></Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>{t("actions.cancel")}</Button><Button onClick={save} disabled={saving}>{t("actions.save")}</Button></Modal.Footer>
            </Modal>
        </>
    )
}
